/**
 * ConvertibleDemosCard — Iter-105 · Item 7.
 *
 * Shows demo sandboxes that have an uploaded property-plan file and
 * offers a one-click "Convert to Live" button. On success the plan
 * file reference + all submitted registration fields are copied into
 * a real property record without requiring a re-upload.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Rocket, FileCheck, CheckCircle2 } from 'lucide-react';
import { apiSafeFetch, apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';

export default function ConvertibleDemosCard() {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [converted, setConverted] = useState({});

  const load = async () => {
    const { data } = await apiSafeFetch('/api/cs/convertible-demos');
    if (data?.rows) setRows(data.rows);
  };

  useEffect(() => { load(); }, []);

  const convert = async (row) => {
    setBusyId(row.demo_session_id);
    try {
      const res = await apiFetch(`/api/cs/convert-demo/${row.demo_session_id}`, { method: 'POST' });
      setConverted((c) => ({ ...c, [row.demo_session_id]: res.property_id }));
      toast.success(`Converted to live property ${res.property_id}`);
      // Refresh list so the sandbox drops off.
      load();
    } catch (e) {
      toast.error(e.detail || 'Conversion failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card
      className="p-4 border-2 border-emerald-500/50 bg-zinc-900"
      data-testid="cs-convertible-demos-card"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-[0.3em] font-bold text-emerald-400 flex items-center gap-2">
          <Rocket className="h-4 w-4" /> Demo → Live Handoff
        </h3>
        <span className="text-[10px] text-zinc-500">
          {rows.length} eligible {rows.length === 1 ? 'sandbox' : 'sandboxes'}
        </span>
      </div>
      <p className="text-[11px] text-zinc-400 mb-3 leading-relaxed">
        Demo sandboxes with a completed property-plan upload. Click <b>Convert to Live</b>
        &nbsp;to create a real property record without a re-upload — the plan file, submitted
        details, and contact info transfer instantly.
      </p>

      {rows.length === 0 && (
        <p
          className="text-xs text-zinc-500 italic py-3 text-center"
          data-testid="cs-convertible-demos-empty"
        >
          No demo sandboxes with attached plans yet.
        </p>
      )}

      <ul className="space-y-2" data-testid="cs-convertible-demos-list">
        {rows.map((r) => (
          <li
            key={r.demo_session_id}
            className="flex items-start justify-between gap-3 p-3 rounded-md bg-zinc-950 border border-zinc-800"
            data-testid={`cs-convertible-row-${r.demo_session_id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{r.property_name || '—'}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {(r.service_line_name || 'NECS Line')} · {r.property_type || 'commercial'}
                {r.tier ? ` · ${r.tier}` : ''}
              </p>
              <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                <FileCheck className="h-3 w-3 text-emerald-400" />
                {r.plan?.original_filename || 'plan attached'}
                {r.plan?.size ? ` · ${Math.round(r.plan.size / 1024)} KB` : ''}
              </p>
            </div>
            <div>
              {converted[r.demo_session_id] ? (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-400"
                  data-testid={`cs-convertible-done-${r.demo_session_id}`}
                >
                  <CheckCircle2 className="h-3 w-3" /> Live
                </span>
              ) : (
                <Button
                  size="sm"
                  onClick={() => convert(r)}
                  disabled={busyId === r.demo_session_id}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-xs uppercase tracking-widest"
                  data-testid={`cs-convertible-convert-${r.demo_session_id}`}
                >
                  {busyId === r.demo_session_id ? 'Converting…' : 'Convert to Live'}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
